export type OwnitCloudProvider = 'local' | 'supabase';
export type OwnitDeploymentMode = 'development' | 'production';

export interface OwnitSupabaseConfig {
    provider: 'supabase';
    url: string;
    anonKey: string;
    schema: string;
}

export type OwnitCloudConfig = OwnitSupabaseConfig | null;

export interface OwnitAdminPolicy {
    defaultAdminEnabled: boolean;
    defaultAdminPassword: string;
    deploymentMode: OwnitDeploymentMode;
    isDefaultPassword: boolean;
    message: string;
}

export type RuntimeEnv = Record<string, string | undefined>;

const DEFAULT_SCHEMA = 'public';
const DEFAULT_ADMIN_PASSWORD = 'admin1234';
const DEFAULT_ADMIN_DISABLED_MESSAGE = '기본 관리자 계정은 배포 환경에서 비활성화되었습니다. Supabase 관리자 역할 또는 OWNIT_DEFAULT_ADMIN_PASSWORD 환경 설정을 사용해 주세요.';

export function getOwnitCloudConfig(): OwnitCloudConfig {
    const env = getOwnitRuntimeEnv({ includeLocalOverrides: true });
    const provider = normalize(env.OWNIT_CLOUD_PROVIDER || env.NG_APP_OWNIT_CLOUD_PROVIDER || env.VITE_OWNIT_CLOUD_PROVIDER);
    const url = normalize(env.OWNIT_SUPABASE_URL || env.NG_APP_OWNIT_SUPABASE_URL || env.VITE_OWNIT_SUPABASE_URL);
    const anonKey = normalize(env.OWNIT_SUPABASE_ANON_KEY || env.NG_APP_OWNIT_SUPABASE_ANON_KEY || env.VITE_OWNIT_SUPABASE_ANON_KEY);

    if ((provider && provider !== 'supabase') || !url || !anonKey) return null;

    return {
        provider: 'supabase',
        url: url.replace(/\/+$/, ''),
        anonKey,
        schema: normalize(env.OWNIT_SUPABASE_SCHEMA || env.NG_APP_OWNIT_SUPABASE_SCHEMA || env.VITE_OWNIT_SUPABASE_SCHEMA) || DEFAULT_SCHEMA
    };
}

export function isOwnitCloudConfigured(): boolean {
    return Boolean(getOwnitCloudConfig());
}

export function getOwnitAdminPolicy(cloudConfig: OwnitCloudConfig = getOwnitCloudConfig()): OwnitAdminPolicy {
    const env = getOwnitRuntimeEnv({ includeLocalOverrides: false });
    const deploymentMode = normalizeDeploymentMode(firstEnv(env, [
        'OWNIT_DEPLOYMENT_MODE',
        'OWNIT_DEPLOYMENT_ENV',
        'NG_APP_OWNIT_DEPLOYMENT_MODE',
        'NG_APP_OWNIT_DEPLOYMENT_ENV',
        'VITE_OWNIT_DEPLOYMENT_MODE',
        'VITE_OWNIT_DEPLOYMENT_ENV'
    ])) || inferDeploymentMode();
    const configuredPassword = normalize(firstEnv(env, [
        'OWNIT_DEFAULT_ADMIN_PASSWORD',
        'NG_APP_OWNIT_DEFAULT_ADMIN_PASSWORD',
        'VITE_OWNIT_DEFAULT_ADMIN_PASSWORD'
    ]));
    const defaultAdminPassword = configuredPassword || DEFAULT_ADMIN_PASSWORD;
    const isDefaultPassword = defaultAdminPassword === DEFAULT_ADMIN_PASSWORD;
    const explicitEnabled = normalizeBoolean(firstEnv(env, [
        'OWNIT_DEFAULT_ADMIN_ENABLED',
        'NG_APP_OWNIT_DEFAULT_ADMIN_ENABLED',
        'VITE_OWNIT_DEFAULT_ADMIN_ENABLED'
    ]));

    let defaultAdminEnabled = explicitEnabled ?? deploymentMode === 'development';
    if (deploymentMode === 'production' && isDefaultPassword) {
        defaultAdminEnabled = false;
    }

    const cloudNote = cloudConfig?.provider === 'supabase'
        ? ' Supabase 배포에서는 JWT app_metadata.role 또는 user_profiles.payload.role 기반 관리자 계정을 사용해야 합니다.'
        : '';
    const message = defaultAdminEnabled
        ? (isDefaultPassword
            ? '개발 환경 기본 관리자 계정이 활성화되어 있습니다.'
            : '환경변수 기반 관리자 비밀번호가 활성화되어 있습니다.')
        : `${DEFAULT_ADMIN_DISABLED_MESSAGE}${cloudNote}`;

    return {
        defaultAdminEnabled,
        defaultAdminPassword,
        deploymentMode,
        isDefaultPassword,
        message
    };
}

export function getOwnitRuntimeEnv(options: { includeLocalOverrides?: boolean } = {}): RuntimeEnv {
    const globalScope = globalThis as any;
    const windowEnv = globalScope.OWNIT_ENV || globalScope.__OWNIT_ENV__ || {};
    const processEnv = globalScope.process?.env || {};
    const localEnv = options.includeLocalOverrides ? readLocalEnv() : {};
    return {
        ...processEnv,
        ...windowEnv,
        ...localEnv
    };
}

function readLocalEnv(): RuntimeEnv {
    try {
        const raw = globalThis.localStorage?.getItem('ownit.cloud.env.v1');
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return {};
        return parsed as RuntimeEnv;
    } catch (error) {
        return {};
    }
}

function normalize(value: string | undefined): string {
    return (value || '').trim();
}

function firstEnv(env: RuntimeEnv, keys: string[]): string {
    for (const key of keys) {
        const value = normalize(env[key]);
        if (value) return value;
    }
    return '';
}

function normalizeBoolean(value: string): boolean | null {
    const normalized = normalize(value).toLowerCase();
    if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) return false;
    return null;
}

function normalizeDeploymentMode(value: string): OwnitDeploymentMode | null {
    const normalized = normalize(value).toLowerCase();
    if (['prod', 'production', 'release'].includes(normalized)) return 'production';
    if (['dev', 'development', 'local', 'test'].includes(normalized)) return 'development';
    return null;
}

function inferDeploymentMode(): OwnitDeploymentMode {
    return isLocalDevelopmentHost() ? 'development' : 'production';
}

function isLocalDevelopmentHost(): boolean {
    try {
        const location = (globalThis as any).location;
        const hostname = normalize(location?.hostname).toLowerCase();
        if (!hostname) return true;
        if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1') return true;
        if (/^10\./.test(hostname) || /^192\.168\./.test(hostname)) return true;
        if (/^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)) return true;
        return false;
    } catch (error) {
        return true;
    }
}
